import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useOverlay } from '../context/OverlayContext';
import GameSetupOverlay from '../components/GameSetupOverlay';
import { handleAuthError } from '../utils/auth';

const mistakeTypes = [
  'First Server Fault',
  'Double Fault',
  'Unforced Error',
  'Forced Error',
  'Poor Footwork',
  'Tactical Mistake',
];

const MatchTrackerPage = () => {
  const { matchId } = useParams(); // Get matchId from the URL
  const navigate = useNavigate();
  const location = useLocation(); // To access navigation state
  
  const [gameNumber, setGameNumber] = useState(1);
  const [setNumber, setSetNumber] = useState(1);
  const [myGamesWonInSet, setMyGamesWonInSet] = useState(0);
  const [opponentGamesWonInSet, setOpponentGamesWonInSet] = useState(0);
  const [mySetsWon, setMySetsWon] = useState(0);
  const [opponentSetsWon, setOpponentSetsWon] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [serving, setServing] = useState(null); // 'me' or 'opponent'
  const [initialSetServer, setInitialSetServer] = useState(null); // 'me' or 'opponent' - who served first in the current set
  const [myMistakes, setMyMistakes] = useState({});
  const [opponentMistakes, setOpponentMistakes] = useState({});
  const [gameWinner, setGameWinner] = useState(null);
  const [previousGameWinner, setPreviousGameWinner] = useState(null); // New state to store winner before starting next game
  const [serverBeforeCurrentGame, setServerBeforeCurrentGame] = useState(null); // New state to store server before starting next game
  const [matchCompleted, setMatchCompleted] = useState(false);
  const [matchScores, setMatchScores] = useState([]); // To store scores for each set
  const [matchDetails, setMatchDetails] = useState(null); // To store initial match details
  const [showGameSetupOverlay, setShowGameSetupOverlay] = useState(false); // New state for the overlay
  const { showOverlay } = useOverlay();

  const totalGamesPlayedInMatch = matchScores.reduce((acc, score) => acc + score.myGames + score.opponentGames, 0) + myGamesWonInSet + opponentGamesWonInSet;

  useEffect(() => {
    const fetchMatchDetails = async () => {
      if (!matchId) {
        showOverlay("No match selected. Redirecting to create a new match.", 'error', () => navigate('/new-match'));
        return;
      }

      try {
        const token = localStorage.getItem('token');
        if (!token) {
          handleAuthError(navigate);
          return;
        }

        const response = await fetch(`/best3intennis-api/api/get_match_details.php?match_id=${matchId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          if (data && (data.message?.includes('token') || data.error?.includes('token'))) {
            handleAuthError(navigate);
            return;
          }
          throw new Error(data?.message || `HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          setMatchDetails(data.match);
          const { current_state } = data;
          setGameNumber(current_state.gameNumber);
          setSetNumber(current_state.setNumber);
          setMyGamesWonInSet(current_state.myGamesWonInSet);
          setOpponentGamesWonInSet(current_state.opponentGamesWonInSet);
          setMySetsWon(current_state.mySetsWon);
          setOpponentSetsWon(current_state.opponentSetsWon);
          setServing(current_state.serving);
          setInitialSetServer(current_state.initialSetServer || null); // Load initialSetServer
          setMatchScores(current_state.matchScores || []); // Initialize with existing scores
          setMatchCompleted(current_state.matchCompleted || false); // Initialize matchCompleted state

          // Determine if GameSetupOverlay should be shown
          const isNewMatch = location.state?.isNewMatch;
          const isContinuedMatch = location.state?.isContinuedMatch;

          if (!current_state.initialSetServer && !current_state.matchCompleted) {
            // If initialSetServer is not set and the match is not completed,
            // we need to ask for the initial server for the current set/match.
            setShowGameSetupOverlay(true);
            setGameStarted(false);
          } else if (current_state.initialSetServer) {
            // Use the serving value from the current state
            setServing(current_state.serving);
            setGameStarted(true);
          } else if (current_state.matchCompleted) {
            // If the match is completed, ensure game tracking is not started.
            setGameStarted(false);
          }

        } else {
          if (data.message?.includes('token') || data.error?.includes('token')) {
            handleAuthError(navigate);
          } else {
            throw new Error(data.message || 'Failed to fetch match details.');
          }
        }
      } catch (error) {
        console.error('Error fetching match details:', error);
        if (error.message.includes('401')) {
          handleAuthError(navigate);
        } else {
          showOverlay(`Error loading match: ${error.message}`, 'error', () => navigate('/history'));
        }
      }
    };

    fetchMatchDetails();
  }, [matchId, navigate, showOverlay, location.state]); // Add location.state to dependencies

  const handleStartGame = async (server) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError(navigate);
        return;
      }

      console.log('Setting initial server to:', server); // Debug log

      // For new set, set both initialSetServer and current server
      setInitialSetServer(server);
      setServing(server);

      const response = await fetch('https://best3intennis.com/best3intennis-api/api/update_match_state.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          matchId,
          initialSetServer: server
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
          handleAuthError(navigate);
          return;
        }
        throw new Error(result?.message || 'Failed to update match state.');
      }

      const result = await response.json();
      // This check might be redundant, but it's a good safeguard
      if (result.message?.includes('token') || result.error?.includes('token')) {
        handleAuthError(navigate);
        return;
      }

      showOverlay('Server selected successfully!', 'success');
      setServing(server);
      setInitialSetServer(server); // Set initial server for the set
      setGameStarted(true);
      setGameWinner(null);
      setMyMistakes({});
      setOpponentMistakes({});
      setShowGameSetupOverlay(false); // Hide the setup overlay once game starts

      // Immediately update matchDetails.current_state to reflect the chosen server
      setMatchDetails(prevDetails => ({
        ...prevDetails,
        current_state: {
          ...prevDetails.current_state,
          serving: server,
          initialSetServer: server,
          gameNumber: 1, // Reset game number for the new set
          setNumber: setNumber, // Ensure set number is correct
          myGamesWonInSet: 0,
          opponentGamesWonInSet: 0,
          mySetsWon: mySetsWon, // Preserve existing sets won
          opponentSetsWon: opponentSetsWon, // Preserve existing sets won
        }
      }));
    } catch (error) {
      console.error('Error setting initial server:', error);
      if (error.message.includes('401')) {
        handleAuthError(navigate);
      } else {
        showOverlay(`Error setting initial server: ${error.message}`, 'error');
      }
    }
  };

  const updateMistakeCount = (player, mistake, action) => {
    const setMistakes = player === 'me' ? setMyMistakes : setOpponentMistakes;
    const currentMistakes = player === 'me' ? myMistakes : opponentMistakes;
    const currentCount = currentMistakes[mistake] || 0;

    let newCount;
    if (action === 'increment') {
      newCount = currentCount + 1;
    } else { // decrement
      newCount = Math.max(0, currentCount - 1);
    }

    setMistakes({
      ...currentMistakes,
      [mistake]: newCount,
    });
  };

  const handleEndGame = async (winner) => {
    if (!serving) {
      showOverlay("Please select who is serving before ending the game.", 'error');
      return;
    }

    // Calculate updated game stats and next server
    const updatedMyGamesWon = winner === 'me' ? myGamesWonInSet + 1 : myGamesWonInSet;
    const updatedOpponentGamesWon = winner === 'opponent' ? opponentGamesWonInSet + 1 : opponentGamesWonInSet;
    const nextServer = serving === 'me' ? 'opponent' : 'me';
    
    console.log('Ending game. Current server:', serving, 'Next server:', nextServer);

    const current_match_state = {
      gameNumber: gameNumber + 1,
      setNumber,
      myGamesWonInSet: updatedMyGamesWon,
      opponentGamesWonInSet: updatedOpponentGamesWon,
      mySetsWon,
      opponentSetsWon,
      serving: nextServer, // Set the next server
      initialSetServer,
      matchScores,
      matchCompleted,
    };

    const gameData = {
      matchId,
      setNumber,
      gameNumber,
      winner,
      myMistakes,
      opponentMistakes,
      servingPlayer: serving, // Current server for this game
      current_match_state
    };

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError(navigate);
        return;
      }

      const response = await fetch('https://best3intennis.com/best3intennis-api/api/save_game.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(gameData),
        credentials: 'include', // Important for sending session cookie
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
          handleAuthError(navigate);
          return;
        }
        throw new Error(result?.message || 'Failed to save game data.');
      }
      
      const result = await response.json();
      if (result.message?.includes('token') || result.error?.includes('token')) {
        handleAuthError(navigate);
        return;
      }

      showOverlay('Game data saved successfully!', 'success');
      
      // Update game statistics
      if (winner === 'me') {
        setMyGamesWonInSet(prev => prev + 1);
      } else {
        setOpponentGamesWonInSet(prev => prev + 1);
      }
      
      // Update the server for the next game and other game state
      const nextServer = serving === 'me' ? 'opponent' : 'me';
      console.log('Ending game. Current server:', serving, 'Next server:', nextServer);
      setServing(nextServer);
      setGameWinner(winner);
      setGameStarted(false);

    } catch (error) {
      console.error('Error saving game:', error);
      if (error.message.includes('401')) {
        handleAuthError(navigate);
      } else {
        showOverlay(`Error: ${error.message}`, 'error');
      }
    }
  };
  
  const handleStartNextGame = () => {
    // Calculate next game values
    const nextGameNumber = gameNumber + 1;
    // The 'serving' state already holds the server for the next game, set by handleEndGame.
    // Store the current gameWinner and serving player before setting them for the new game
    setPreviousGameWinner(gameWinner);
    setServerBeforeCurrentGame(serving); // This is the server for the game about to start

    // Update state for next game
    setGameNumber(nextGameNumber);
    // setServing is not needed here as it's already set by handleEndGame for the next game
    setGameStarted(true);
    setGameWinner(null); // Clear game winner for the new game
    setMyMistakes({});
    setOpponentMistakes({});
    
    // Update match details with new server
    setMatchDetails(prevDetails => ({
      ...prevDetails,
      current_state: {
        ...prevDetails.current_state,
        gameNumber: nextGameNumber,
        serving: serving // Use the current 'serving' state which is already correct
      }
    }));
  };

  const handleNewSet = async () => {
    // Save current set score
    const newMatchScores = [...matchScores, { myGames: myGamesWonInSet, opponentGames: opponentGamesWonInSet }];
    setMatchScores(newMatchScores);

    // Determine set winner and update sets won
    let newMySetsWon = mySetsWon;
    let newOpponentSetsWon = opponentSetsWon;
    if (myGamesWonInSet > opponentGamesWonInSet) {
      newMySetsWon = mySetsWon + 1;
      setMySetsWon(newMySetsWon);
    } else if (opponentGamesWonInSet > myGamesWonInSet) {
      newOpponentSetsWon = opponentSetsWon + 1;
      setOpponentSetsWon(newOpponentSetsWon);
    }

    // Update match state in the database
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError(navigate);
        return;
      }

      // Save current state to match_scores before starting new set
      const response = await fetch('https://best3intennis.com/best3intennis-api/api/save_game.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          matchId,
          setNumber,
          gameNumber,
          winner: myGamesWonInSet > opponentGamesWonInSet ? 'me' : 'opponent',
          servingPlayer: serving,
          myMistakes: {},
          opponentMistakes: {},
          current_match_state: {
            gameNumber: 1,
            setNumber: setNumber + 1,
            myGamesWonInSet: 0,
            opponentGamesWonInSet: 0,
            mySetsWon: newMySetsWon,
            opponentSetsWon: newOpponentSetsWon,
            serving: null,
            initialSetServer: null,
            matchScores: newMatchScores,
            matchCompleted: false
          }
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
          handleAuthError(navigate);
          return;
        }
        throw new Error(result?.message || 'Failed to update match state.');
      }

      // Reset for new set
      setSetNumber(prev => prev + 1);
      setGameNumber(1);
      setMyGamesWonInSet(0);
      setOpponentGamesWonInSet(0);
      setGameStarted(false); // Allow user to choose server for new set
      setGameWinner(null); // Clear game winner display
      setInitialSetServer(null); // Reset initial server for the new set
      setShowGameSetupOverlay(true); // Show the setup overlay for the new set

    } catch (error) {
      console.error('Error updating match state:', error);
      if (error.message.includes('401')) {
        handleAuthError(navigate);
      } else {
        showOverlay(`Error: ${error.message}`, 'error');
      }
    }
  };

  const handleCompleteMatch = async () => {
    const finalMatchScores = [...matchScores];
    if (myGamesWonInSet > 0 || opponentGamesWonInSet > 0) {
        finalMatchScores.push({ myGames: myGamesWonInSet, opponentGames: opponentGamesWonInSet });
    }

    let currentMySetsWon = 0;
    let currentOpponentSetsWon = 0;
    finalMatchScores.forEach(score => {
        if (score.myGames > score.opponentGames) {
            currentMySetsWon++;
        } else if (score.opponentGames > score.myGames) {
            currentOpponentSetsWon++;
        }
    });

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError(navigate);
        return;
      }

      const response = await fetch('https://best3intennis.com/best3intennis-api/api/complete_match.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          matchId,
          mySetsWon: currentMySetsWon,
          opponentSetsWon: currentOpponentSetsWon,
          matchScores: finalMatchScores,
        }),
        credentials: 'include',
      });

      if (!response.ok) {
        const result = await response.json().catch(() => null);
        if (result && (result.message?.includes('token') || result.error?.includes('token'))) {
          handleAuthError(navigate);
          return;
        }
        throw new Error(result?.message || 'Failed to complete match.');
      }

      const result = await response.json();
      if (result.message?.includes('token') || result.error?.includes('token')) {
        handleAuthError(navigate);
        return;
      }

      setMatchCompleted(true);
      showOverlay(
        `Match Over! Final Score: Me ${currentMySetsWon} - ${currentOpponentSetsWon} Opponent`,
        'success',
        () => navigate('/dashboard', { state: { matchCompleted: true } })
      );
    } catch (error) {
      console.error('Error completing match:', error);
      if (error.message.includes('401')) {
        handleAuthError(navigate);
      } else {
        showOverlay(`Error: ${error.message}`, 'error');
      }
    }
  };

  const renderMistakeTrackers = () => {
    return (
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Header for mobile */}
          <div className="grid grid-cols-3 gap-4 md:hidden mb-2">
            <div className="font-semibold text-gray-700">Mistake</div>
            <div className="font-semibold text-gray-700 text-center">Me</div>
            <div className="font-semibold text-gray-700 text-center">Opponent</div>
          </div>
          {mistakeTypes.map((mistake) => (
            <div key={mistake} className="grid grid-cols-3 md:grid-cols-2 gap-4 items-center py-3 border-b border-gray-200 last:border-b-0">
              {/* Mistake Name */}
              <div className="text-gray-700 md:font-semibold">{mistake}</div>

              {/* 'Me' Actions */}
              <div className="flex items-center justify-center md:justify-end space-x-2">
                <button
                  type="button"
                  className="bg-red-200 hover:bg-red-300 text-red-800 rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold transition duration-300"
                  onClick={() => updateMistakeCount('me', mistake, 'decrement')}
                >
                  -
                </button>
                <span className="font-semibold text-green-800 min-w-[24px] text-center text-lg">{myMistakes[mistake] || 0}</span>
                <button
                  type="button"
                  className="bg-green-200 hover:bg-green-300 text-green-800 rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold transition duration-300"
                  onClick={() => updateMistakeCount('me', mistake, 'increment')}
                >
                  +
                </button>
              </div>

              {/* 'Opponent' Actions */}
              <div className="flex items-center justify-center md:justify-end space-x-2">
                <button
                  type="button"
                  className="bg-red-200 hover:bg-red-300 text-red-800 rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold transition duration-300"
                  onClick={() => updateMistakeCount('opponent', mistake, 'decrement')}
                >
                  -
                </button>
                <span className="font-semibold text-green-800 min-w-[24px] text-center text-lg">{opponentMistakes[mistake] || 0}</span>
                <button
                  type="button"
                  className="bg-green-200 hover:bg-green-300 text-green-800 rounded-full w-8 h-8 flex items-center justify-center text-xl font-bold transition duration-300"
                  onClick={() => updateMistakeCount('opponent', mistake, 'increment')}
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-4xl mx-auto my-8">
      <h2 className="text-2xl font-semibold text-green-800 mb-6 text-center">
        Match Tracker - {matchDetails ? matchDetails.opponent_name : 'Loading...'} - Set {setNumber} (Games: Me {myGamesWonInSet} - {opponentGamesWonInSet} Opponent)
      </h2>
      <p className="text-center text-lg mb-2">
        Overall Sets: Me {mySetsWon} - {opponentSetsWon} Opponent
      </p>
      {matchScores.length > 0 && (
        <p className="text-center text-lg mb-4">
          Previous Sets: {matchScores.map((score, index) => `${score.myGames}-${score.opponentGames}`).join(', ')}
        </p>
      )}

      {matchCompleted ? (
        <div className="text-center space-y-4">
          <h2 className="text-3xl font-bold text-green-800">Match Completed!</h2>
          <p className="text-xl text-gray-700">
            Final Score: Me {mySetsWon} - {opponentSetsWon} Opponent
          </p>
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </button>
        </div>
      ) : (
        <>
          {showGameSetupOverlay && matchDetails && (
            <GameSetupOverlay
              onSelectServer={handleStartGame}
              onClose={() => setShowGameSetupOverlay(false)}
              opponentName={matchDetails.opponent_name}
            />
          )}

          {!showGameSetupOverlay && gameStarted && !gameWinner && (
            <div>
              <p className="text-center text-lg mb-6">
                <strong>Serving:</strong> {serving === null ? 'Not selected' : (
                  <span className="font-semibold" data-testid="serving-player">
                    {serving === 'me' ? 'Me' : 'Opponent'}
                  </span>
                )}
              </p>
              <div>
                <h3 className="text-xl font-medium text-gray-700 text-center mb-4">Mistakes</h3>
                <div className="space-y-3">
                  {renderMistakeTrackers()}
                </div>
              </div>
              <hr className="my-8 border-gray-300" />
              <div className="flex flex-wrap gap-4 justify-center">
                <button className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300" onClick={() => handleEndGame('me')}>I Won The Game</button>
                <button className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300" onClick={() => handleEndGame('opponent')}>Opponent Won</button>
                <button className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300" onClick={() => {
                  setGameStarted(false);
                  setMyMistakes({});
                  setOpponentMistakes({});
                  if (gameNumber === 1 && myGamesWonInSet === 0 && opponentGamesWonInSet === 0) {
                    // If this is the first game of a new set, show the game setup overlay
                    setShowGameSetupOverlay(true);
                    setGameWinner(null); // Ensure gameWinner is null to not show "Game Over"
                    setServing(null); // For a new set, server needs to be chosen again
                  } else {
                    // Revert to the state before the current game started, showing the previous game's winner
                    setGameWinner(previousGameWinner);
                    setPreviousGameWinner(null); // Clear previous winner after use
                    setServing(serverBeforeCurrentGame); // Restore the server from before this game started
                    setServerBeforeCurrentGame(null); // Clear previous server after use
                  }
                }}>Cancel Game</button>
              </div>
            </div>
          )}

          {!showGameSetupOverlay && gameWinner && (
            <div className="text-center"> {/* Removed space-y-4 for debugging */}
              <h2 className="text-2xl font-bold text-green-800">
                {gameWinner === null ? 'Game Cancelled' : `Game ${totalGamesPlayedInMatch} Over!`}
              </h2>
              {gameWinner !== null && (
                <p className="text-xl text-gray-700">
                  Winner: <strong className="text-green-600">{gameWinner === 'me' ? 'You' : 'Opponent'}</strong>
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
                <button className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300" onClick={handleStartNextGame}>
                  Start Next Game
                </button>
                <button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300" onClick={handleNewSet}>
                  New Set
                </button>
                <button className="bg-purple-500 hover:bg-purple-600 text-white font-semibold py-2 px-6 rounded-md transition duration-300" onClick={handleCompleteMatch}>
                  Complete Match
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MatchTrackerPage;


